import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import BaseModal from '../components/modals/BaseModal';
import ProductForm from '../components/forms/ProductForm';

const PRODUCTS_STORAGE_KEY = 'saleswork_demo_products';

const loadStoredProducts = fallback => {
  try {
    const stored = localStorage.getItem(PRODUCTS_STORAGE_KEY);
    if (!stored) return fallback;

    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    console.error('[PRODUCT STORAGE] Failed to load products', error);
    return fallback;
  }
};

const saveStoredProducts = products => {
  try {
    localStorage.setItem(PRODUCTS_STORAGE_KEY, JSON.stringify(products));
  } catch (error) {
    console.error('[PRODUCT STORAGE] Failed to save products', error);
  }
};

const categoryLabels = {
  standard: 'Standard',
  sales: 'Salg',
  'meeting-booking': 'Møtebooking',
  'customer-service': 'Kundeservice',
  recruitment: 'Rekruttering',
  support: 'Support',
  consulting: 'Konsulenttjeneste',
  saas: 'Programvare/SaaS',
  course: 'Kurs',
  other: 'Annet'
};

const productTypeLabels = {
  product: 'Produkt',
  service: 'Tjeneste',
  subscription: 'Abonnement',
  hourly: 'Timebasert',
  commission: 'Provisjon',
  performance: 'Resultatbasert',
  bundle: 'Pakke',
  other: 'Annet'
};

const pricingModelLabels = {
  fixed: 'Fastpris',
  hourly: 'Per time',
  monthly: 'Per måned',
  'per-user': 'Per bruker',
  'per-lead': 'Per lead',
  'per-meeting': 'Per møte',
  'per-ticket': 'Per sak/ticket',
  'commission-percent': 'Provisjon %',
  'value-percent': 'Prosent av verdi',
  combination: 'Kombinasjon',
  custom: 'Etter avtale'
};

const unitLabels = {
  stk: 'stk',
  hour: 'time',
  month: 'måned',
  user: 'bruker',
  lead: 'lead',
  meeting: 'møte',
  ticket: 'sak/ticket',
  project: 'prosjekt',
  candidate: 'kandidat',
  agreement: 'avtale',
  other: 'annet'
};

const formatNok = value => `${Number(value || 0).toLocaleString()} NOK`;

const Products = () => {
  const { products, addProduct, updateProduct, deleteProduct } = useCRM();
  const [localProducts, setLocalProducts] = useState(() =>
    loadStoredProducts(Array.isArray(products) ? products : [])
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');

  const displayedProducts = localProducts;

  const categories = useMemo(() => {
    const cats = new Set(displayedProducts.map(p => p.category).filter(Boolean));
    return ['All', ...Array.from(cats)];
  }, [displayedProducts]);

  const filteredProducts = useMemo(() => {
    return displayedProducts.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) || product.description?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory = categoryFilter === 'All' || product.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [displayedProducts, searchQuery, categoryFilter]);

  const closeProductModal = () => {
    setIsModalOpen(false);
  };

  const handleSave = (productData) => {
    const now = new Date().toISOString();
    const productId = editingProduct?.id || `product-${Date.now()}`;
    const vatRate = Number(productData.vatRate ?? productData.vat ?? productData.tax ?? 25);
    const normalizedProduct = {
      ...editingProduct,
      ...productData,
      id: productId,
      product_id: editingProduct?.product_id || productId,
      name: productData.name.trim(),
      description: productData.description || '',
      productType: productData.productType || 'product',
      pricingModel: productData.pricingModel || 'fixed',
      unit: productData.unit || 'stk',
      price: Number(productData.price || 0),
      fixedPrice: Number(productData.fixedPrice || 0),
      percentageRate: Number(productData.percentageRate || 0),
      vatRate,
      vat: vatRate,
      tax: vatRate,
      category: productData.category || 'standard',
      canBeBundled: Boolean(productData.canBeBundled),
      type: 'product',
      status: editingProduct?.status || productData.status || 'active',
      createdAt: editingProduct?.createdAt || now,
      updatedAt: now
    };

    if (editingProduct) {
      if (typeof updateProduct === 'function') {
        updateProduct(editingProduct.id, normalizedProduct);
      }

      const updatedProducts = displayedProducts.map(product =>
        product.id === editingProduct.id ? normalizedProduct : product
      );
      setLocalProducts(updatedProducts);
      saveStoredProducts(updatedProducts);
    } else if (typeof addProduct === 'function') {
      addProduct(normalizedProduct);
    } else {
      const updatedProducts = [normalizedProduct, ...displayedProducts];
      setLocalProducts(updatedProducts);
      saveStoredProducts(updatedProducts);
    }

    closeProductModal();
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Er du sikker på at du vil slette dette produktet?')) {
      if (typeof deleteProduct === 'function') {
        deleteProduct(id);
      }

      const updatedProducts = displayedProducts.filter(product => product.id !== id);
      setLocalProducts(updatedProducts);
      saveStoredProducts(updatedProducts);
    }
  };

  const handleOpenNewProduct = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const getDisplayPrice = product => {
    const pricingModel = product.pricingModel || 'fixed';

    if (pricingModel === 'custom') {
      return {
        label: 'Pris / sats',
        value: 'Etter avtale'
      };
    }

    if (pricingModel === 'commission-percent' || pricingModel === 'value-percent') {
      return {
        label: 'Prosent',
        value: `${Number(product.percentageRate || 0)} %`
      };
    }

    if (pricingModel === 'combination') {
      return {
        label: 'Pris / sats',
        value: `${formatNok(product.fixedPrice)} + ${Number(product.percentageRate || 0)} %`
      };
    }

    const unit = unitLabels[product.unit];
    return {
      label: 'Pris / sats',
      value: `${formatNok(product.price)}${unit && product.unit !== 'stk' ? ` / ${unit}` : ''}`
    };
  };

  return (
    <div className="p-8 space-y-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Produktkatalog</h1>
          <p className="text-slate-500 font-medium mt-1">Administrer produkter og tjenester for bruk i tilbud og kontrakter.</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative hidden md:block group">
            <SafeIcon icon="🔍" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-primary-500 transition-colors" />
            <input type="text" placeholder="Søk i produkter..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 outline-none transition-all w-64 font-bold text-sm" />
          </div>
          <button type="button" onClick={handleOpenNewProduct} className="flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-600 text-white font-black text-sm rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-200 transition-all active:scale-95">
            <SafeIcon icon="➕" className="w-4 h-4" /> Ny produkt/tjeneste
          </button>
        </div>
      </div>

      {/* Categories / Filters */}
      <div className="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
        {categories.map(cat => (
          <button key={cat} onClick={() => setCategoryFilter(cat)} className={`px-4 py-2 rounded-lg text-xs font-black uppercase tracking-widest whitespace-nowrap transition-all border ${categoryFilter === cat ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'}`}>
            {cat === 'All' ? 'Alle Kategorier' : categoryLabels[cat] || cat}
          </button>
        ))}
      </div>

      {/* Product Grid */}
      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product) => {
            const vat = product.vatRate ?? product.vat ?? product.tax ?? 25;
            const priceDisplay = getDisplayPrice(product);

            return (
              <motion.div layout initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} key={product.id} className="group bg-white border border-slate-200 rounded-2xl p-5 hover:shadow-xl hover:shadow-slate-200/50 transition-all flex flex-col">
                <div className="flex justify-between items-start mb-4">
                  <div className="w-12 h-12 bg-primary-50 rounded-xl flex items-center justify-center text-primary-600 group-hover:scale-110 transition-transform">
                    <SafeIcon icon="🛍️" className="w-6 h-6" />
                  </div>
                  <div className="relative">
                    <button className="p-2 text-slate-300 hover:text-slate-600 transition-colors">
                      <SafeIcon icon="⋮" className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <h3 className="font-black text-slate-900 text-lg mb-1">{product.name}</h3>
                <p className="text-xs text-slate-500 font-medium line-clamp-2 mb-4 h-8">
                  {product.description || 'Ingen beskrivelse.'}
                </p>
                <div className="flex flex-wrap items-center gap-2 mb-6">
                  <span className="px-2 py-1 bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200">
                    {categoryLabels[product.category] || product.category || 'Generell'}
                  </span>
                  <span className="px-2 py-1 bg-primary-50 text-primary-600 text-[10px] font-bold uppercase tracking-wider rounded-md border border-primary-100">
                    {productTypeLabels[product.productType] || product.productType || 'Produkt'}
                  </span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200">
                    {pricingModelLabels[product.pricingModel] || product.pricingModel || 'Fastpris'}
                  </span>
                  <span className="px-2 py-1 bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wider rounded-md border border-slate-200">
                    {vat}% MVA
                  </span>
                </div>
                <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
                  <div>
                    <span className="block text-[10px] font-bold text-slate-400 uppercase">{priceDisplay.label}</span>
                    <span className="font-black text-slate-900 text-lg">
                      {priceDisplay.value}
                    </span>
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => handleDelete(product.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"><SafeIcon icon="🗑️" className="w-4 h-4" /></button>
                    <button onClick={() => handleEdit(product)} className="p-2 text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-all"><SafeIcon icon="✏️" className="w-4 h-4" /></button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            <SafeIcon icon="📦" className="w-10 h-10 text-slate-300" />
          </div>
          <h3 className="text-lg font-black text-slate-900">Ingen produkter funnet</h3>
          <p className="text-sm font-medium mt-1">Prøv å endre søkeord eller opprett et nytt produkt.</p>
        </div>
      )}

      {/* Product Modal */}
      <BaseModal isOpen={isModalOpen} onClose={closeProductModal} title={editingProduct ? 'Rediger produkt/tjeneste' : 'Ny produkt/tjeneste'} disableAnimation={isModalOpen && !editingProduct}>
        <ProductForm initialData={editingProduct} onSave={handleSave} onCancel={closeProductModal} />
      </BaseModal>
    </div>
  );
};

export default Products;